import React from 'react';

import { Route, Routes } from 'react-router-dom';


import {
  AuthorProfiles,
  BlogManager,
}
from '../../containers';
// ─── Auth / Signup ────────────────────────────────────────────────────────
import SignIn from '../../containers/CustomerSignIn';
import WebsiteBlogs from '../../screens/Blogs';
// ─── Businesses ───────────────────────────────────────────────────────────
import {
  AffiliateBusinessDetails,
  BusinessesDetails,
  BusinessesListAll,
  BusinessesListRecommended,
  BusinessesListSearch,
  FollowedBusinesses,
  AffiliateBusinessesListAll
} from '../../screens/Businesses';
// ─── Coupons ──────────────────────────────────────────────────────────────
import {
  CategorySearch,
  CouponApply,
  CouponsDetails,
  CouponsListAll,
  CouponsMapAll,
  LocationSearch,
  MyFavourites,
  OnboardingSlides,
  ReservedCoupon,
} from '../../screens/Coupons';
import {
  ContactInformationForm,
  EmailPasswordForm,
  PreferencesForm as SignUpPreferencesForm,
  PrivacyPolicyForm as SignUpPrivacyPolicyForm,
  ReferralCodeForm,
} from '../../screens/CustomerSignUp';
import GuestHome from '../../screens/GuestHome';
// ─── Home ─────────────────────────────────────────────────────────────────
import Home from '../../screens/Home';
// ─── Account ──────────────────────────────────────────────────────────────
import MyAccount from '../../screens/MyAccount';
// ─── Payment / Post-signup ────────────────────────────────────────────────
import { FreePremiumScreen, PaymentForm, ReferralProgramForm,SuccessfulSignUp } from '../../screens/Payment';
import Preferences from '../../screens/Preferences';
// ─── Legal ────────────────────────────────────────────────────────────────
import CustomerPrivacyPolicy from '../../screens/PrivacyPolicy';
import { BusinessesReferrals, CustomersReferrals } from '../../screens/Referrals';
import CustomerTermsOfService from '../../screens/TermsOfService';
// ─── Used coupons / referrals ─────────────────────────────────────────────
import { HistoryCouponsList, ReservedCouponsList } from '../../screens/UsedCoupons';
import NotFound from '../../screens/NotFound/NotFound.jsx';
import ForgotPassword from '../ForgotPassword/ForgotPassword';
import CustomerLayout from './CustomerLayout';
import CustomerSessionGuard from '../CustomerSessionGuard/CustomerSessionGuard';
import { RouteNames } from './routeNames';
import DealsByCityRedirect from '../../screens/DealsByCityRedirect';

/**
 * All customer-facing routes, kept in their own file so App.js (business /
 * admin side) doesn't have to grow further. Mount this once inside App.js's
 * existing <Switch> with a catch-all, e.g.:
 *
 *   import CustomerRoutes from 'routing/CustomerRoutes';
 *   ...
 *   <Route path="/*" component={CustomerRoutes} />   // v5 still wraps v6 fine
 *                                                      // as long as this
 *                                                      // file's own <Routes>
 *                                                      // handles the rest.
 *
 * Simplest integration: replace the existing `/customer-login` Route in
 * App.js with this component mounted at the app root (or under a shared
 * "/c" prefix if you'd rather keep it fully separable) — see the migration
 * note at the bottom of this file.
 *
 * HEADER LAYOUT: routes that should render <CustomerHeader /> are nested
 * inside the wrapping <Route element={<CustomerLayout />}> below — that's
 * everything a logged-in customer browses day to day (Home, Coupons,
 * Businesses, Account, Used coupons, Referrals, Legal). Routes that happen
 * before/outside a logged-in session — sign-in, the sign-up flow, the
 * onboarding slides, forgot-password — stay as plain siblings so they don't
 * get the header (no profile/location/logout makes sense there yet).
 *
 * To add a new screen under the header: nest its <Route> inside the
 * CustomerLayout block. To add one without the header: add it as a sibling
 * at the top level, same as the auth routes below.
 */
const CustomerRoutes = () => (
  <Routes>
    {/* Auth / Signup — no header */}
    {/* <Route path={RouteNames.SIGN_IN} element={<SignIn />} /> */}
    <Route path={RouteNames.FORGOT_PASSWORD} element={<ForgotPassword />} />
    <Route path={RouteNames.SIGN_UP} element={<EmailPasswordForm />} />
    <Route path={RouteNames.SIGN_UP_EMAIL_PASSWORD_INFO} element={<EmailPasswordForm />} />
    <Route path={RouteNames.SIGN_UP_CONTACT_INFO} element={<ContactInformationForm />} />
    <Route path={RouteNames.SIGN_UP_PREFERENCES} element={<SignUpPreferencesForm />} />
    <Route path={RouteNames.SIGN_UP_REFERRAL_CODE} element={<ReferralCodeForm />} />
    <Route path={RouteNames.SIGN_UP_PRIVACY_POLICY} element={<SignUpPrivacyPolicyForm />} />
    <Route path={RouteNames.ONBOARDING} element={<OnboardingSlides />} />

    {/* Payment / post-signup — no header (mid-flow, not regular browsing) */}
    <Route path={RouteNames.SUCCESSFUL_SIGN_UP} element={<SuccessfulSignUp />} />
    <Route path={RouteNames.PAYMENT_FORM} element={<PaymentForm />} />
    <Route path={RouteNames.FREE_PREMIUM} element={<FreePremiumScreen />} />
    <Route path={RouteNames.REFERRAL_PROGRAM_FORM} element={<ReferralProgramForm />} />

    {/* Full-page LocationSearch route kept as-is (no header) — separate from
        the modal version CustomerHeader opens inline. Anything that still
        deep-links here directly continues to work unchanged. */}
    <Route path={RouteNames.LOCATION_SEARCH} element={<LocationSearch />} />

    {/* ─────────────────────────────────────────────────────────────────
        Everything below gets <CustomerHeader /> via CustomerLayout.
       ───────────────────────────────────────────────────────────────── */}
    <Route element={<CustomerLayout />}>
      {/* Home */}
      <Route path={'/'} element={<GuestHome />} />
      <Route path={RouteNames.GUEST_HOME} element={<GuestHome />} />

      {/* Coupons */}
      <Route path={RouteNames.COUPONS_LISTS_ALL} element={<CouponsListAll />} />
      <Route path={RouteNames.COUPONS_MAPS_ALL} element={<CouponsMapAll />} />
      <Route path={`${RouteNames.COUPONS_DETAILS}/:id`} element={<CouponsDetails />} />
      <Route path={RouteNames.CATEGORY_SEARCH} element={<CategorySearch />} />

      {/* Businesses */}
      <Route path={`${RouteNames.BUSINESSES_DETAILS}/:id`} element={<BusinessesDetails />} />
      <Route path={`${RouteNames.AFFILIATE_BUSINESS_DETAILS}/:id`} element={<AffiliateBusinessDetails />} />
      <Route path={RouteNames.BUSINESSES_LIST_ALL} element={<BusinessesListAll />} />
      <Route path={RouteNames.AFFILIATE_BUSINESSES_LIST_ALL} element={<AffiliateBusinessesListAll />} />
      <Route path={RouteNames.BUSINESSES_LIST_SEARCH} element={<BusinessesListSearch />} />
      <Route path={RouteNames.BUSINESSES_LIST_RECOMMENDED} element={<BusinessesListRecommended />} />

      {/* Account */}

      {/* Used coupons / referrals */}

      {/* ───────────────────────────────────────────────────────────────
          Login-required customer pages. CustomerSessionGuard sends guests
          AND business/admin sessions to the guest page instead of mounting
          the screen (so no customer-only fetches fire with the wrong token).
          Public pages stay outside this block.
         ─────────────────────────────────────────────────────────────── */}
      <Route element={<CustomerSessionGuard />}>
        <Route path={RouteNames.HOME} element={<Home />} />
        <Route path={`${RouteNames.COUPONS_APPLY}/:id`} element={<CouponApply />} />
        <Route path={RouteNames.COUPONS_RESERVED} element={<ReservedCoupon />} />
        <Route path={RouteNames.FAVOURITES} element={<MyFavourites />} />
        <Route path={RouteNames.BUSINESSES_FOLLOWED} element={<FollowedBusinesses />} />
        <Route path={RouteNames.ACCOUNT} element={<MyAccount />} />
        <Route path={RouteNames.PREFERENCES} element={<Preferences />} />
        <Route path={RouteNames.USED_COUPONS_HISTORY} element={<HistoryCouponsList />} />
        <Route path={RouteNames.USED_COUPONS_RESERVED} element={<ReservedCouponsList />} />
        <Route path={RouteNames.REFERRALS_BUSINESSES} element={<BusinessesReferrals />} />
        <Route path={RouteNames.REFERRALS_CUSTOMERS} element={<CustomersReferrals />} />
      </Route>

      {/* Legal */}
      <Route path={RouteNames.PRIVACY_POLICY} element={<CustomerPrivacyPolicy />} />
      <Route path={RouteNames.TERMS_OF_SERVICE} element={<CustomerTermsOfService />} />

      {/* Blogs and authors are admin-only, so they don't get the customer header either */}
      <Route path="/admin/blogs" element={<BlogManager />} />
      <Route path="/admin/authors" element={<AuthorProfiles/>} />
      <Route path="/blogs" element={<WebsiteBlogs />} />
      <Route path="/blogs/:authorSlug/:blogSlug" element={<WebsiteBlogs />} />

      
      <Route path={RouteNames.LOCATION_SEARCH} element={<LocationSearch />} />
      <Route path={`${RouteNames.DEALS_BY_CITY}/:citySlug`} element={<DealsByCityRedirect />} />

    </Route>



    {/* Catch-all within the customer area — prevents falling through to
        App.js's generic "Not found" for paths under here that don't match
        anything above. */}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default CustomerRoutes;